import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const STORAGE_KEY = "klc-admin-sidebar-open";

const navSections = [
  {
    id: "main",
    label: "Main",
    items: [{ id: "dashboard", label: "Dashboard", icon: "📊", to: "/dashboard" }]
  },
  {
    id: "management",
    label: "Management",
    items: [
      { id: "users", label: "Users", icon: "👥", to: "/users" },
      { id: "coaching-hacks", label: "Coaching Hacks", icon: "💡", to: "/coaching-hacks" },
      { id: "daily-prompts", label: "Daily Prompts", icon: "📝", to: "/daily-prompts" },
      { id: "growth-snapshots", label: "Growth Snapshots", icon: "🌱", to: "/growth-snapshots" },
      { id: "reflections-screens", label: "Reflections", icon: "🪞", to: "/reflections-screens" },
      { id: "info-videos", label: "Info Videos", icon: "🎬", to: "/info-videos" },
      { id: "cms-content", label: "CMS Content", icon: "📄", to: "/cms-content" },
      { id: "help-questions", label: "Help", icon: "❓", to: "/help-questions" },
      { id: "sales-content", label: "Sales Content", icon: "🛒", to: "/sales-content" },
      { id: "resource-hub", label: "Resource Hub", icon: "📚", to: "/resource-hub" },
      { id: "purchases", label: "Purchases", icon: "💳", to: "/purchases" },
      { id: "course-sales", label: "Course Sales", icon: "💸", to: "/course-sales" },
      { id: "coupons", label: "Coupons", icon: "🎟️", to: "/coupons" },
      { id: "coupon-redemptions", label: "Applied Coupons", icon: "🏷️", to: "/coupon-redemptions" },
      { id: "user-activity", label: "User Activity", icon: "📜", to: "/user-activity" },
      { id: "course-progress", label: "Course Progress", icon: "📈", to: "/course-progress" }
    ]
  },
  {
    id: "courses",
    label: "Courses",
    items: [
      { id: "courses", label: "All Courses", icon: "🎓", to: "/courses" },
      
      { id: "step-sections", label: "Step Sections", icon: "📋", to: "/step-sections" }
    ]
  },
  {
    id: "system",
    label: "System",
    items: [
      { id: "settings", label: "Settings", icon: "⚙️", to: "/settings" },
      { id: "xero-settings", label: "Xero", icon: "📒", to: "/xero-settings" }
    ]
  }
];

function buildOpenState(openSectionId) {
  return navSections.reduce((acc, section) => {
    acc[section.id] = openSectionId != null && section.id === openSectionId;
    return acc;
  }, {});
}

function readStoredOpen() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      const openIds = navSections.filter((section) => parsed[section.id]).map((section) => section.id);
      if (openIds.length === 1) return parsed;
      if (openIds.length > 1) return buildOpenState(openIds[0]);
    }
  } catch (e) {
    // ignore
  }
  return buildOpenState(null);
}

function itemMatchesPath(item, pathname) {
  if (item.to === "/users/new") return pathname === "/users/new";
  if (item.to === "/users") return pathname.startsWith("/users") && pathname !== "/users/new";
  if (item.to === "/courses/new") return pathname === "/courses/new";
  if (item.to === "/courses") return pathname.startsWith("/courses") && pathname !== "/courses/new";
  if (item.to === "/dashboard") return pathname === "/" || pathname.startsWith("/dashboard");
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const [openSections, setOpenSections] = useState(readStoredOpen);

  const { activeId, activeSectionId } = useMemo(() => {
    for (const section of navSections) {
      for (const item of section.items) {
        if (itemMatchesPath(item, pathname)) {
          return { activeId: item.id, activeSectionId: section.id };
        }
      }
    }
    return { activeId: "dashboard", activeSectionId: "main" };
  }, [pathname]);

  useEffect(() => {
    setOpenSections((prev) => {
      const openCount = navSections.filter((section) => prev[section.id]).length;
      if (prev[activeSectionId] && openCount === 1) return prev;
      const next = buildOpenState(activeSectionId);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore
      }
      return next;
    });
  }, [activeSectionId]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => {
      const next = prev[sectionId] ? buildOpenState(null) : buildOpenState(sectionId);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src="/images/klc-logo.png" alt="KLC Admin" style={{ width: "120px", height: "auto" }} />
      </div>
      <nav className="sidebar-nav">
        {navSections.map((section) => {
          const isOpen = Boolean(openSections[section.id]);
          return (
            <div key={section.id} className={`nav-section${isOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="nav-section-toggle"
                aria-expanded={isOpen}
                aria-controls={`nav-section-${section.id}`}
                onClick={() => toggleSection(section.id)}
              >
                <span>{section.label}</span>
                <span className="nav-chevron" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    className="arrow"
                    width="16"
                    height="16"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M17.418 6.109c.272-.268.709-.268.979 0s.271.701 0 .969l-7.908 7.83c-.27.268-.707.268-.979 0l-7.908-7.83c-.27-.268-.27-.701 0-.969.271-.268.709-.268.979 0L10 13.25l7.418-7.141z" />
                  </svg>
                </span>
              </button>
              {isOpen ? (
                <div id={`nav-section-${section.id}`} className="nav-section-items">
                  {section.items.map((item) => (
                    <Link key={item.to} to={item.to} className={item.id === activeId ? "active" : ""}>
                      <span className="nav-icon">{item.icon}</span>
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
